import type { SupabaseClient } from "@supabase/supabase-js";
import { diffDayDoors, type DayDoors, type LocalFestivalDay } from "./doorsForDay";

// Persisting the producer's per-day doors window (briefing_day_doors).
//
// Extracted from CreateEditBriefing.tsx because two save paths need it
// and only one of them used to have it: the normal Next/Back save (via
// saveRelatedData, gated on the "days" scope) and the first save of a
// brand-new production, which navigates and returns before
// saveRelatedData is ever reached. See saveScope.ts for what that cost
// the producer. Keeping the write in one place means the two paths
// cannot drift.

export type { LocalFestivalDay };

/**
 * Sync briefing_day_doors with the editor's local festivalDays state:
 * write the days whose window moved, and drop rows for days the
 * producer removed.
 *
 * An EMPTY `days` list is treated as "nothing to sync", not as "delete
 * every day". A festival always has at least one day, so an empty list
 * means the form hasn't loaded — and a half-loaded form must never
 * clear the producer's real windows.
 *
 * `existing` is the stored baseline. Pass it when the caller already
 * knows it — the create path knows it is empty, because the briefing
 * was inserted milliseconds earlier — and the read is skipped. Omit it
 * and this reads the baseline itself.
 *
 * Returns the rows as they now stand in the database, so the caller can
 * seed its cache with them instead of re-reading. That matters on the
 * create path: the editor's briefing_day_doors query fires the moment
 * the new briefing id exists, which is BEFORE this write lands, so it
 * caches an empty result.
 *
 * Throws when a write fails — a silently dropped doors write is what
 * this module exists to prevent, so the caller's error toast is the
 * right outcome rather than a save that only looks successful.
 */
export async function saveDayDoors(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  client: SupabaseClient<any, any, any>,
  briefingId: string,
  days: LocalFestivalDay[],
  existing?: DayDoors[],
): Promise<DayDoors[]> {
  if (!briefingId || days.length === 0) return [];

  // A failed read is not fatal: with no baseline we can still write the
  // days the producer just typed, we simply can't tell which stale rows
  // to delete. Losing a delete leaves a row for a day that no longer
  // exists (harmless — nothing renders it); losing the write is the
  // data loss this module exists to prevent.
  let baseline = existing;
  if (!baseline) {
    const { data } = await client
      .from("briefing_day_doors")
      .select("event_date, doors_open, doors_close")
      .eq("briefing_id", briefingId);
    baseline = (data ?? []) as DayDoors[];
  }

  const { desired, toUpsert, toDelete } = diffDayDoors({
    briefingId,
    localDays: days,
    existing: baseline,
  });

  if (toUpsert.length > 0) {
    const { error } = await client
      .from("briefing_day_doors")
      .upsert(toUpsert, { onConflict: "briefing_id,event_date" });
    if (error) throw new Error(`briefing_day_doors upsert failed: ${error.message}`);
  }

  if (toDelete.length > 0) {
    const { error } = await client
      .from("briefing_day_doors")
      .delete()
      .eq("briefing_id", briefingId)
      .in("event_date", toDelete);
    if (error) throw new Error(`briefing_day_doors delete failed: ${error.message}`);
  }

  return desired.map((row) => ({
    event_date: row.event_date,
    doors_open: row.doors_open,
    doors_close: row.doors_close,
  }));
}
